import React, { useEffect, useRef, useState } from "react";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../utils/customError";
import { useFormik } from "formik";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { RunnerDetails, RunnerEdit } from "../../services/runner.services";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Loader } from "@googlemaps/js-api-loader";

const EditRunner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const inputRef = useRef(null);

  const [submitted, setSubmitted] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);

  const breadcrumbs = [
    { title: t("Dashboard"), url: "/admin/dashboard" },
    { title: t("Runner"), url: "/admin/temple-runner/list/1" },
    { title: t("Edit"), url: "" },
  ];

  /* ================= VALIDATION ================= */

  const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];
  const validationSchema = Yup.object().shape({
    runnerName: Yup.string()
      .required("Runner name is required")
      .test(
        "no-only-spaces",
        "Runner name cannot be empty or spaces",
        (value) => value && value.trim() !== ""
      ),
    runnerEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .test(
        "no-only-spaces",
        "Email cannot be empty or spaces",
        (value) => value && value.trim() !== ""
      ),
    runnerPhoneNumber: Yup.string()
      .required("Phone number is required")
      .test(
        "no-only-spaces",
        "Phone number cannot be empty or spaces",
        (value) => value && value.trim() !== ""
      ),
    city: Yup.string()
      .required("City is required")
      .test(
        "no-only-spaces",
        "City cannot be empty or spaces",
        (value) => value && value.trim() !== ""
      ),
    state: Yup.string()
        .nullable(),
    country: Yup.string()
        .nullable(),
    runnerImage: Yup.mixed()
      .nullable()
      .test(
        "fileFormat",
        "Only JPG, JPEG, PNG files are allowed",
        (value) => {
          if (!value) return true; // nullable
          return SUPPORTED_FORMATS.includes(value.type);
        }
      ),
  });

  /* ================= FORM ================= */

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      runnerName: "",
      runnerEmail: "",
      runnerPhoneNumber: "",
      runnerImage: "",
      city: "",
      state: "",
      country: "",
      latitude: "",
      longitude: "",
    },
    validationSchema,
    validateOnBlur: false,
    onSubmit: (values) => {
      globalLoader(true);
      setSubmitted(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        let value = values[key];
        if (value === "" || value === "null") {
            value = null;
        }
        if(value){
          formData.append(key, value);
        }
        
      });

      formData.append("o_id", id);

      RunnerEdit(formData, id)
        .then((response) => {
          globalLoader(false);
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message,
              ...SWAL_SETTINGS,
            });
            navigate("/admin/temple-runner/list/1");
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
            setSubmitted(false);
          }
        })
        .catch((error) => {
          globalLoader(false);
          setSubmitted(false);
          Swal.fire({
            icon: "error",
            text:
              error?.response?.data?.message ||
              error?.message ||
              "Server Error",
            ...SWAL_SETTINGS,
          });
        });
    },
  });

  /* ================= FETCH RUNNER ================= */

  useEffect(() => {
    setLoading(true);
    RunnerDetails(id)
      .then((res) => {
        const data = res?.data || {};
        formik.setValues({
          runnerName: data.name || "",
          runnerEmail: data.email || "",
          runnerPhoneNumber: data.phone_number || "",
          runnerImage: "",
          city: data.city || "",
          state: data.state || "",
          country: data.country || "",
          latitude: data.latitude || "",
          longitude: data.longitude || "",
        });

        setPreviewImage(data.profile_image || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  /* ================= GOOGLE PLACES ================= */

  useEffect(() => {
    if (loading) return;
    if (!inputRef.current) return;

    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
      version: "weekly",
      libraries: ["places"],
    });

    let autocomplete;

    loader.importLibrary("places").then(({ Autocomplete }) => {
      autocomplete = new Autocomplete(inputRef.current, {
        fields: ["geometry", "address_components", "formatted_address"],
        types: ["geocode"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.geometry) return;

        const comps = place.address_components || [];
        const getComp = (type) =>
          comps.find((c) => c.types.includes(type))?.long_name || "";
          const city = getComp("locality") || getComp("administrative_area_level_2") || place.formatted_address || "";
          const stateValue = getComp("administrative_area_level_1");
          const countryValue = getComp("country");
          formik.setFieldValue("city", city);
          formik.setFieldValue("state", stateValue);
          formik.setFieldValue("country", countryValue);
          formik.setFieldValue("latitude", place.geometry.location.lat());
          formik.setFieldValue("longitude", place.geometry.location.lng());
          if (!stateValue) {
            formik.setFieldValue("state","");
          }
          if (!countryValue) {
            formik.setFieldValue("country", "");
          }
      });
    });

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [loading]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      <form onSubmit={formik.handleSubmit}>
        <div className="card custom-card">
          <div className="card-body">
            <h6 className="main-content-label mb-3">
              {t("Edit Runner")}
            </h6>

            <div className="row">
              {/* Name */}
              <div className="col-md-6 form-group">
                <label>
                  Runner Name <span className="requirestar"> *</span>
                </label>
                <input
                  className="form-control"
                  name="runnerName"
                  value={formik.values.runnerName}
                  onChange={formik.handleChange}
                />
                <span className="text-danger d-flex text-left">
                  <CustomError name="runnerName" form={formik} />
                </span>
              </div>

              {/* Email */}
              <div className="col-md-6 form-group">
                <label>
                  Email <span className="requirestar"> *</span>
                </label>
                <input
                  className="form-control"
                  name="runnerEmail"
                  value={formik.values.runnerEmail}
                  onChange={formik.handleChange}
                />
                <span className="text-danger d-flex text-left">
                  <CustomError name="runnerEmail" form={formik} />
                </span>
              </div>

              {/* Phone */}
              <div className="col-md-6 form-group">
                <label>
                  Phone <span className="requirestar"> *</span>
                </label>
                <input
                  className="form-control"
                  name="runnerPhoneNumber"
                  value={formik.values.runnerPhoneNumber}
                  onChange={formik.handleChange}
                />
                <span className="text-danger d-flex text-left">
                  <CustomError
                    name="runnerPhoneNumber"
                    form={formik}
                  />
                </span>
              </div>

              {/* City */}
              <div className="col-md-6 form-group">
                <label>
                  City <span className="requirestar"> *</span>
                </label>
                <input
                  name="city"
                  ref={inputRef}
                  className="form-control"
                  defaultValue={formik.values.city}
                  onChange={(e) => {
                    formik.setFieldValue("city", e.target.value);
                  }}
                />
                <span className="text-danger d-flex text-left">
                  <CustomError name="city" form={formik} />
                </span>
              </div>

              {/* State */}
              <div className="col-md-6 form-group">
                <label>
                  State <span className="requirestar"> *</span>
                </label>
                <input
                  className="form-control"
                  value={formik.values.state}
                  readOnly
                />
              </div>

              {/* Country */}
              <div className="col-md-6 form-group">
                <label>
                  Country <span className="requirestar"> *</span>
                </label>
                <input
                  className="form-control"
                  value={formik.values.country}
                  readOnly
                />
              </div>

              {/* Image */}
              <div className="col-md-12 form-group">
                <label>
                  Runner Image <span className="requirestar"> *</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  name="runnerImage"
                  onChange={(e) => {
                    const file = e.currentTarget.files[0];
                    formik.setFieldValue("runnerImage", file);
                    if (file) {
                      setPreviewImage(URL.createObjectURL(file));
                    }
                  }}
                />
                <span className="text-danger d-flex text-left">
                  <CustomError name="runnerImage" form={formik} />
                </span>

                {previewImage && (
                  <img
                    src={previewImage}
                    alt="preview"
                    className="mt-2"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                  />
                )}
              </div>

              {/* Buttons */}
              <div className="mt-3">
                <button
                  className="btn btn-info me-2"
                  type="submit"
                  disabled={submitted}
                >
                  Submit
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditRunner;
